import { Content, Root, Trigger } from '@radix-ui/react-popover';
import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { useId } from 'react';
import ColorSwatch from '../ColorSwatch';
import { overlayStyles } from '../styles/overlays';
import { colors } from '../theme/colors.stylex';
import type { ColorPickerControlProps } from './ColorPickerControl.types';

const styles = stylex.create({
  root: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  label: {
    cursor: 'pointer',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    whiteSpace: 'nowrap',
    color: colors.bodyText,
  },
  content: { position: 'relative', zIndex: 1200, minWidth: '1.5rem', color: colors.bodyText },
});

function ColorPickerControl({
  color,
  label,
  children,
  placement = 'bottom',
  align = 'start',
  extraClassNames = '',
  xstyle,
  contentXstyle,
  id,
  testId,
  onOpen,
}: ColorPickerControlProps) {
  const swatchId = useId();
  const rootProps = stylex.props(styles.root, xstyle);

  const triggerLabel = (
    <label {...stylex.props(styles.label)} htmlFor={swatchId}>
      {label}
    </label>
  );

  const swatch = <ColorSwatch aria-label={label} color={color} id={id ?? swatchId} size="sm" onClick={onOpen} />;

  if (!children) {
    return (
      <div {...rootProps} className={clsx(rootProps.className, extraClassNames)}>
        <span {...stylex.props(styles.label)}>{label}</span>
        {swatch}
      </div>
    );
  }

  return (
    <div {...rootProps} className={clsx(rootProps.className, extraClassNames)}>
      {triggerLabel}
      <Root>
        <Trigger asChild>{swatch}</Trigger>
        <Content
          align={align}
          {...stylex.props(overlayStyles.panel, overlayStyles.motion, styles.content, contentXstyle)}
          data-testid={testId}
          side={placement}
          sideOffset={2}>
          {children}
        </Content>
      </Root>
    </div>
  );
}

export default ColorPickerControl;
